import React from 'react';
import { Store } from '../stores/stores';
import { StoreBrand, STORE_BRAND_LABELS } from '../stores/constants';
import differenceBy from 'lodash/differenceBy';
import intersectionBy from 'lodash/intersectionBy';
import difference from 'lodash/difference';
import PhoneNumber from 'awesome-phonenumber';
import { toJS } from 'mobx';
import produce from 'immer';
import moment from 'moment';
import { Tag } from '../components/tag';

export interface BaseStore {
    nref: string;
}

export const services = {
    'service-coffee-club': 'Café club',
    'service-guichet': 'Guichet automatique',
    'service-poste_ts': 'Vente de timbres postaux',
    'service-guichet_rbc': 'Guichet automatique RBC',
    'service-guichet_ret': 'Guichet automatique retrait seulement',
    'serv-repaschaud': 'Grillades',
    'service-propane': 'Propane',
    'service-propane_exc': 'Propane échange',
    'horaire-detaillant_24': 'Ouvert 24 heures',
    'service-guichet_mnvie': 'Guichet automatique Manuvie',
    'service-toilette': 'Toilette accessible à la clientèle',
    'essence-any': 'Essence',
    'essence-esso': 'Esso',
    'serv-polarpop': 'Polar Pop',
    'service-propane_refill': 'Propane remplissage',
    'service-guichet_cibc': 'Guichet automatique CIBC',
    'essence-ultramar': 'Ultramar',
    'service-guichet_scotia': 'Guichet automatique Scotia',
    diesel: 'Diésel',
    'essence-couche_tard': 'Essence Couche-Tard',
    'service-guichet_bnc': 'Guichet automatique Banque Nationale',
    'service-guichet_multi': 'Guichet automatique multiservices',
    'service-lave_auto': 'Lave-auto',
    'service-lave_auto_auto': 'Lave-auto automatique',
    'service-lave_auto_program': 'Abonnement Lave-auto',
    'essence-petrocanada': 'Petro-Canada',
    'service-poste': 'Bureau de poste',
    'resto-any': 'Restaurant',
    'resto-buffet': 'Buffet',
    'resto-foire_alimentaire': 'Foire alimentaire',
    'resto-mm': 'M&M',
    'resto-tim_hortons': 'Tim Hortons',
    'service-lave_auto_manuel': 'Lave-auto à la main',
    'resto-coq_lala': 'Coq Lala',
    'resto-mcdonalds': 'McDonalds',
    'resto-sushi_shop': 'Sushi Shop',
    'service-messagerie_pc': 'Service de colis / messagerie',
    'essence-petro_t': 'Pétro-T',
    'essence-irving': 'Irving',
    'resto-big_stop': 'Big Stop',
    'resto-restaurant_sans_banniere': 'Restaurant sans bannière',
    'resto-epicerie_fine_des_chutes': 'Épicerie fine des Chutes',
    'resto-subway': 'Subway',
    'service-service_pompe': 'Service aux pompes',
    'service-guichet_multi_nd':
        'Guichet automatique multiservices (sauf dépôt)',
    'service-guichet_desjardins': 'Guichet automatique Desjardins',
    'essence-shell': 'Shell',
    'resto-regal_des_chutes': 'Régal des Chutes',
    'resto-la_belle_province': 'La belle province',
    'resto-mikes': 'Mikes',
    'resto-pfk': 'PFK',
    'resto-poulet_chester': 'Poulet Chester',
    'essence-sonic': 'Sonic',
    'resto-quiznos': 'Quiznos',
    'resto-presse_cafe': 'Presse Café',
    'resto-saint_hubert': 'Saint Hubert',
    'resto-aw': 'A&W',
    'resto-valentine': 'Valentine',
    'resto-ottario': 'Ottario',
    'service-saq': 'SAQ',
} as const;

const FrenchStatus = {
    'open-24': 'Ouvert 24h',
    open: 'Ouvert',
    closed: 'Fermé',
    'temporarily-closed': 'Fermé temporairement',
    'coming-soon': 'Ouverture prochainement',
} as const;

const CarWashStatuskeys = {
    OPEN: 'open',
    CLOSED: 'closed',
    NO_CAR_WASH: 'no-car-wash',
} as const;

const StoreStatuskeys = {
    OPEN_24: 'open-24',
    OPEN: 'open',
    CLOSED: 'closed',
    TEMPORARILY_CLOSED: 'temporarily-closed',
    COMING_SOON: 'coming-soon',
} as const;

export const invertedServices = Object.fromEntries(
    Object.entries(services).map(([key, value]) => [value, key])
);

// The 24h marker service. Implied by `open-24` status: import always
// adds it for open-24 stores, so export must too for a stable roundtrip.
const ALWAYS_OPEN_SERVICE = 'horaire-detaillant_24';

// Canonical per-day hours for an open-24 store. Export and import
// must emit the exact same string or the roundtrip sprouts phantom diffs.
const ALWAYS_OPEN_HOURS = '0:00-0:00';

// Service keys the CMS dictionary recognises. Keys outside this set (the
// `services` column is free-form JSON) cannot survive the CSV label roundtrip,
// so the diff ignores them rather than reporting phantom removals / dropping
// them on upload.
const KNOWN_SERVICE_KEYS = new Set(Object.keys(services));
const isKnownService = (service: string) => KNOWN_SERVICE_KEYS.has(service);

export interface ParsedStore {
    id: number;
    nref: string;
    brand: StoreBrand;
    addr: {
        city: string | undefined;
        post: string | undefined;
        street: string | undefined;
    };
    lat: number | undefined;
    lng: number | undefined;
    hours: [
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined
    ];
    tel: string | undefined;
    services: string[] | undefined;
    status: string | undefined;
    carWashStatus: string | undefined;
}

export type ImportWarnings = {
    missingColumns: string[];
    unknownColumns: string[];
};

export class Difference {
    constructor(public initial: Store) {}

    phoneNumber?: string;
    city?: string;
    post?: string;
    street?: string;
    addedServices?: string[];
    removedServices?: string[];
    status?: string;
    carWashStatus?: string;
    lat?: number;
    lng?: number;

    hours: [
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined
    ] = [
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
    ];

    getModifiedStore() {
        return produce(this.initial, (draft) => {
            if (this.phoneNumber) {
                draft.tel = this.phoneNumber;
            }
            if (this.city) {
                draft.addr.city = this.city;
            }
            if (this.post) {
                draft.addr.post = this.post;
            }
            if (this.street) {
                draft.addr.street = this.street;
            }
            if (this.status) {
                draft.status = this.status;
            }
            if (this.carWashStatus) {
                draft.carWashStatus = this.carWashStatus;
            }
            if (this.addedServices) {
                draft.services = draft.services.concat(this.addedServices);
            }
            if (this.lat !== undefined) {
                draft.lat = this.lat;
            }
            if (this.lng !== undefined) {
                draft.lng = this.lng;
            }
            if (this.removedServices) {
                for (const removedService of this.removedServices) {
                    const index = draft.services.findIndex(
                        (s) => s === removedService
                    );
                    // Guard: splice(-1, 1) would silently drop the LAST service.
                    if (index !== -1) draft.services.splice(index, 1);
                }
            }
            for (let i = 0; i < this.hours.length; i++) {
                const hour = this.hours[i];
                if (hour !== undefined) {
                    draft.hours[i] = hour;
                }
            }
        });
    }
}

const isHoursFormat = (hours: string) => {
    return (
        /^[0-9,:,-]+$/.test(hours) &&
        hours.includes('-') &&
        hours.split('-')[0].length >= 4 &&
        hours.split('-')[1].length >= 4
    );
};

const isSameHours = (hoursA: string, hoursB: string) => {
    return (
        moment('1970-01-01 ' + hoursA).format('HH:mm') ===
        moment('1970-01-01 ' + hoursB).format('HH:mm')
    );
};

export class DifferenceMap extends Map<string, Difference> {
    getOrCreate(store: Store): Difference {
        if (this.has(store.nref)) {
            return this.get(store.nref)!;
        } else {
            const difference = new Difference(toJS(store));
            this.set(store.nref, difference);
            return difference;
        }
    }
}

export const normalize = (title?: string) => {
    return title
        ?.normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase()
        .replace(/-/g, '')
        .replace(/,/g, '')
        .replace(/\s/g, '');
};

const normalizedLabelToServiceKey = new Map<string | undefined, string>();
for (const [key, label] of Object.entries(services)) {
    normalizedLabelToServiceKey.set(normalize(label), key);
}

const canonicalizeService = (service: string): string => {
    if (KNOWN_SERVICE_KEYS.has(service)) return service;
    return normalizedLabelToServiceKey.get(normalize(service)) ?? service;
};

class Field {
    index = -1;
    constructor(public readonly title: string) {}
}

class FileConfiguration {
    private innerMap = new Map<string | undefined, Field>();
    // Effective width of the header row (index of its last non-empty cell + 1);
    // trailing cells beyond it on a data row can only be unquoted-CSV overflow.
    private headerLength = 0;

    STORE_NUMBER = new Field('numéro magasin');
    BRAND = new Field('marque');
    ADDRESS = new Field('adresse');
    CITY = new Field('ville');
    ZIP_CODE = new Field('code postal');
    LATITUDE = new Field('latitude');
    LONGITUDE = new Field('longitude');
    PHONE = new Field('téléphone');
    STATUS = new Field('statut');
    CAR_WASH_STATUS = new Field('statut du lave-auto');
    SUNDAY = new Field('dimanche - ouverture');
    SUNDAY_CLOSE = new Field('dimanche - fermeture');
    MONDAY = new Field('lundi - ouverture');
    MONDAY_CLOSE = new Field('lundi - fermeture');
    TUESDAY = new Field('mardi - ouverture');
    TUESDAY_CLOSE = new Field('mardi - fermeture');
    WEDNESDAY = new Field('mercredi - ouverture');
    WEDNESDAY_CLOSE = new Field('mercredi - fermeture');
    THURSDAY = new Field('jeudi - ouverture');
    THURSDAY_CLOSE = new Field('jeudi - fermeture');
    FRIDAY = new Field('vendredi - ouverture');
    FRIDAY_CLOSE = new Field('vendredi - fermeture');
    SATURDAY = new Field('samedi - ouverture');
    SATURDAY_CLOSE = new Field('samedi - fermeture');
    SERVICES = new Field('services');

    constructor() {
        Object.defineProperty(this, 'innerMap', {
            enumerable: false,
        });
        Object.defineProperty(this, 'headerLength', {
            enumerable: false,
        });

        for (const field of Object.values(this)) {
            this.innerMap.set(normalize(field.title), field);
        }
    }

    setFieldIndexForFirstRow(row: string[]): string[] {
        const unknownColumns: string[] = [];
        for (let i = 0; i < row.length; i++) {
            const col = row[i]?.trim();
            if (!col) continue;
            this.headerLength = i + 1;
            const matched = this.setFieldIndex(col, i);
            if (!matched) unknownColumns.push(col);
        }
        return unknownColumns;
    }

    // When `services` is the last header column (it always is in the CMS's
    // own export), a spreadsheet that re-saves the file without quoting the
    // comma-joined services cell spills every service after the first into its
    // own trailing column. Papa hands those back as separate cells that nothing
    // reads, so the extra services silently vanish (adding an existing service
    // doesn't show up — Excel quotes the cell, Numbers does not). Rejoin the
    // overflow back into the services cell. Gate on the header
    // (not on mapped columns): only cells beyond the header width can be
    // overflow, so a legitimate unknown column sitting to the right of
    // `services` is never absorbed. If services is not the last header column
    // (columns were reordered or followed by an unknown one) the overflow is
    // ambiguous, so skip.
    coalesceTrailingServices(row: string[]): void {
        // A spreadsheet that trims trailing empty cells on re-save (Numbers
        // does this) yields a row that ends before the services/status
        // columns. Pad it back to the header width so downstream column reads
        // (status, opening hours, services) never hit `undefined`.
        if (this.headerLength > 0 && row.length < this.headerLength) {
            while (row.length < this.headerLength) row.push('');
        }
        const s = this.SERVICES.index;
        if (s === -1 || s !== this.headerLength - 1) return;
        if (row.length <= this.headerLength) return;
        row[s] = row.slice(s).join(',');
        row.length = s + 1;
    }

    getMissingFields(): string[] {
        return [...this.innerMap.values()]
            .filter(
                (f) =>
                    f !== this.STORE_NUMBER &&
                    f !== this.BRAND &&
                    f.index === -1
            )
            .map((f) => f.title);
    }

    private setFieldIndex(title: string, index: number): boolean {
        const field = this.innerMap.get(normalize(title));
        if (field && field.index === -1) {
            field.index = index;
            return true;
        }
        return false;
    }
}

export const getAddedStores = (
    existingList: Store[],
    parsedList: ParsedStore[]
) => {
    const addedStores = differenceBy<ParsedStore, Store>(
        parsedList,
        existingList,
        (s) => s.nref
    );

    return addedStores;
};

const normalizeStreet = (street: string) => {
    const nStreet = street
        .toLocaleLowerCase()
        .replace(/\s(boulevard|boul\.|boul)\s/g, 'boulevard')
        .replace(/\s(avenue|av\.|av)\s/g, 'avenue')
        .replace(/\s(saint|st)\s/g, 'saint')
        .replace(/\s(saint|st)-/g, 'saint')
        .replace(/\srue\s/g, '')
        .replace(/\s(chemin|ch\.|ch)\s/g, 'chemin');

    return normalize(nStreet);
};

const normalizeCity = (city: string) => {
    const nCity = city
        .toLocaleLowerCase()
        .replace(/(saint|st)\s/g, 'saint')
        .replace(/(saint|st)-/g, 'saint');

    return normalize(nCity);
};

const diffStore = (
    existing: Store,
    parsed: ParsedStore,
    diffMap: DifferenceMap
) => {
    // Status-implied fields are non-authoritative and ignored by the
    // apps, so diffing them only produces noise. Per-day hours are implied for
    // open-24 (always 0:00-0:00) AND closed (always empty); the 24h service is
    // implied for open-24 only. We diff neither when the status implies them.
    const effectiveStatus = parsed.status ?? existing.status;
    const is24 = effectiveStatus === StoreStatuskeys.OPEN_24;
    const hoursAreImplied = is24 || effectiveStatus === StoreStatuskeys.CLOSED;

    // Compare phone numbers
    if (parsed.tel !== undefined) {
        const existingPhone = new PhoneNumber(existing.tel ?? '', 'CA');
        const parsedPhone = new PhoneNumber(parsed.tel, 'CA');
        if (existingPhone.getNumber() !== parsedPhone.getNumber()) {
            diffMap.getOrCreate(existing).phoneNumber = parsed.tel;
        }
    }

    // Compare hours — parsedHour === undefined means the column was absent; skip that slot
    if (!hoursAreImplied)
        for (let i = 0; i < 7; i++) {
            const existingHour = existing.hours[i];
            const parsedHour = parsed.hours[i];

            if (existingHour != null && parsedHour != null) {
                if (isHoursFormat(existingHour) && isHoursFormat(parsedHour)) {
                    const existingHourStart = existingHour.split('-')[0];
                    const parsedHourStart = parsedHour.split('-')[0];
                    const existingHourEnd = existingHour.split('-')[1];
                    const parsedHourEnd = parsedHour.split('-')[1];

                    if (
                        isSameHours(existingHourStart, parsedHourStart) &&
                        isSameHours(existingHourEnd, parsedHourEnd)
                    ) {
                        continue;
                    }
                }

                if (existingHour !== parsedHour) {
                    diffMap.getOrCreate(existing).hours[i] = parsedHour;
                }
            }
        }

    // Compare city
    if (
        parsed.addr.city !== undefined &&
        normalizeCity(existing.addr.city ?? '') !==
            normalizeCity(parsed.addr.city)
    ) {
        diffMap.getOrCreate(existing).city = parsed.addr.city;
    }

    // Compare zip
    if (
        parsed.addr.post !== undefined &&
        normalize(existing.addr.post ?? '') !== normalize(parsed.addr.post)
    ) {
        diffMap.getOrCreate(existing).post = parsed.addr.post;
    }

    // Compare street
    if (
        parsed.addr.street !== undefined &&
        normalizeStreet(existing.addr.street ?? '') !==
            normalizeStreet(parsed.addr.street)
    ) {
        diffMap.getOrCreate(existing).street = parsed.addr.street;
    }

    // Compare status
    if (parsed.status !== undefined && existing.status !== parsed.status) {
        diffMap.getOrCreate(existing).status = parsed.status;
    }

    // Compare car wash status
    if (
        parsed.carWashStatus !== undefined &&
        existing.carWashStatus !== parsed.carWashStatus
    ) {
        diffMap.getOrCreate(existing).carWashStatus = parsed.carWashStatus;
    }

    // Compare lat/lng — exact comparison; suspect rounding artifacts are handled
    // by findSuspectCoordChanges() and surfaced to the user via CoordPrecisionModal.
    if (
        parsed.lat !== undefined &&
        parsed.lat !== 0 &&
        existing.lat !== parsed.lat
    ) {
        diffMap.getOrCreate(existing).lat = parsed.lat;
    }

    if (
        parsed.lng !== undefined &&
        parsed.lng !== 0 &&
        existing.lng !== parsed.lng
    ) {
        diffMap.getOrCreate(existing).lng = parsed.lng;
    }

    // Compare services — only diff keys the CMS dictionary recognises. Unknown
    // keys can't survive the CSV label roundtrip, so excluding them avoids
    // phantom removals and prevents silently dropping them on upload.
    if (parsed.services !== undefined) {
        // The 24h service is implied by open-24 status, so its presence/absence
        // must not surface as a diff. Suppress it whenever either side is open-24
        // so transitioning to or from open-24 never persists or drops it.
        const eitherSideIs24 =
            existing.status === StoreStatuskeys.OPEN_24 ||
            parsed.status === StoreStatuskeys.OPEN_24;
        const diffable = (service: string) =>
            isKnownService(service) &&
            !(eitherSideIs24 && service === ALWAYS_OPEN_SERVICE);
        const existingPairs = existing.services
            .map((original) => ({
                original,
                key: canonicalizeService(original),
            }))
            .filter(({ key }) => diffable(key));
        const knownExisting = existingPairs.map(({ key }) => key);
        const knownParsed = parsed.services
            .map(canonicalizeService)
            .filter(diffable);
        const removedKeys = difference(knownExisting, knownParsed);
        const addedServices = difference(knownParsed, knownExisting);
        const removedServices = existingPairs
            .filter(({ key }) => removedKeys.includes(key))
            .map(({ original }) => original);

        if (removedServices.length > 0) {
            diffMap.getOrCreate(existing).removedServices = removedServices;
        }

        if (addedServices.length > 0) {
            diffMap.getOrCreate(existing).addedServices = addedServices;
        }
    }
};

export const storeFilter = (filter?: string) => (store: Store) => {
    const normalizeFilter = normalize(filter) ?? '';
    return (
        !filter ||
        store.nref.includes(filter) ||
        normalize(store.addr.city ?? '')?.includes(normalizeFilter) ||
        normalize(store.addr.street ?? '')?.includes(normalizeFilter) ||
        normalize(store.addr.post ?? '')?.includes(normalizeFilter)
    );
};

export const diffStores = (
    existingList: Store[],
    parsedList: ParsedStore[]
) => {
    const mapOfExisting = new Map(existingList.map((e) => [e.nref, e]));
    const mapOfParsed = new Map(parsedList.map((p) => [p.nref, p]));

    const intersection = intersectionBy<BaseStore>(
        parsedList,
        existingList,
        (store) => store.nref
    );

    const diffMap = new DifferenceMap();
    for (const store of intersection) {
        const existing = mapOfExisting.get(store.nref);
        const parsed = mapOfParsed.get(store.nref);
        // Since we are in the intersection, we can assume that the store will exist
        diffStore(existing!, parsed!, diffMap);
    }

    return diffMap;
};

// Helper method to extract the hours
function getOpeningHoursFactory(status: Field, row: string[]) {
    return function (open: Field, close: Field): string | undefined {
        if (status.index === -1 || open.index === -1) return undefined;
        const is24 =
            normalize(row[status.index]) === normalize(FrenchStatus['open-24']);
        const isClosed =
            normalize(row[status.index]) === normalize(FrenchStatus['closed']);
        if (is24) {
            return ALWAYS_OPEN_HOURS;
        }
        if (isClosed) {
            return '';
        }
        if (row[open.index] === '') {
            return '';
        }
        // The below if should be eventually deleted when the data is normalized (i.e.: data is either in a XX:XX format or empty string)
        if (normalize(row[open.index]) === normalize(FrenchStatus['closed'])) {
            return '';
        }
        if (close.index === -1) return undefined;
        return `${row[open.index]}-${row[close.index]}`;
    };
}

// Helper method to extract the service using closure to access the row
function valueExtractorFactory(row: string[]) {
    return function (field: Field, map: { [key: string]: string }) {
        const normalizedMap = new Map<string | undefined, string>();
        for (const [key, value] of Object.entries(map)) {
            normalizedMap.set(normalize(key), value);
        }

        const values = (row[field.index] ?? '')
            .split(',')
            .map((s) => normalize(s));
        const extractedValues = values
            .map((value) => normalizedMap.get(value))
            .filter((v): v is string => !!v);
        return extractedValues;
    };
}

const parseCoord = (raw: string | undefined): number | undefined => {
    if (raw === undefined) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
};

export const parseStoreFile = (
    data: string[][]
): { stores: ParsedStore[]; warnings: ImportWarnings } => {
    const configuration = new FileConfiguration();
    const stores: ParsedStore[] = [];
    let unknownColumns: string[] = [];

    for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const id = i + 1;
        if (i < 1) {
            unknownColumns = configuration.setFieldIndexForFirstRow(row);
        } else {
            const nref = row[configuration.STORE_NUMBER.index];

            if (!nref) {
                break;
            }

            // Repair unquoted comma-joined services that a spreadsheet spilled
            // across trailing columns on re-save.
            configuration.coalesceTrailingServices(row);

            // Create the store
            const getOpeningHours = getOpeningHoursFactory(
                configuration.STATUS,
                row
            );
            const valueExtractor = valueExtractorFactory(row);

            const store: ParsedStore = {
                id,
                nref,
                brand:
                    configuration.BRAND.index !== -1 &&
                    normalize(row[configuration.BRAND.index]) ===
                        normalize(STORE_BRAND_LABELS[StoreBrand.CIRCLE_K])
                        ? StoreBrand.CIRCLE_K
                        : StoreBrand.COUCHE_TARD,
                addr: {
                    city:
                        configuration.CITY.index !== -1
                            ? row[configuration.CITY.index]
                            : undefined,
                    post:
                        configuration.ZIP_CODE.index !== -1
                            ? row[configuration.ZIP_CODE.index]
                            : undefined,
                    street:
                        configuration.ADDRESS.index !== -1
                            ? row[configuration.ADDRESS.index]
                            : undefined,
                },
                lat: parseCoord(
                    configuration.LATITUDE.index !== -1
                        ? row[configuration.LATITUDE.index]
                        : undefined
                ),
                lng: parseCoord(
                    configuration.LONGITUDE.index !== -1
                        ? row[configuration.LONGITUDE.index]
                        : undefined
                ),
                tel:
                    configuration.PHONE.index !== -1
                        ? row[configuration.PHONE.index]
                        : undefined,
                carWashStatus:
                    configuration.CAR_WASH_STATUS.index === -1
                        ? undefined
                        : valueExtractor(configuration.CAR_WASH_STATUS, {
                              Ouvert: CarWashStatuskeys.OPEN,
                              Fermé: CarWashStatuskeys.CLOSED,
                              'Aucun lave-auto': CarWashStatuskeys.NO_CAR_WASH,
                          })[0] ?? undefined,
                status:
                    configuration.STATUS.index === -1
                        ? undefined
                        : valueExtractor(configuration.STATUS, {
                              Ouvert: StoreStatuskeys.OPEN,
                              'Ouvert 24h': StoreStatuskeys.OPEN_24,
                              'Fermé temporairement':
                                  StoreStatuskeys.TEMPORARILY_CLOSED,
                              Fermé: StoreStatuskeys.CLOSED,
                              'Ouverture prochainement':
                                  StoreStatuskeys.COMING_SOON,
                          })[0] ?? undefined,
                hours: [
                    getOpeningHours(
                        configuration.SUNDAY,
                        configuration.SUNDAY_CLOSE
                    ),
                    getOpeningHours(
                        configuration.MONDAY,
                        configuration.MONDAY_CLOSE
                    ),
                    getOpeningHours(
                        configuration.TUESDAY,
                        configuration.TUESDAY_CLOSE
                    ),
                    getOpeningHours(
                        configuration.WEDNESDAY,
                        configuration.WEDNESDAY_CLOSE
                    ),
                    getOpeningHours(
                        configuration.THURSDAY,
                        configuration.THURSDAY_CLOSE
                    ),
                    getOpeningHours(
                        configuration.FRIDAY,
                        configuration.FRIDAY_CLOSE
                    ),
                    getOpeningHours(
                        configuration.SATURDAY,
                        configuration.SATURDAY_CLOSE
                    ),
                ],
                // The 24h marker service is derived-only: export
                // emits it from status, import does not synthesize it, and the
                // diff ignores it on open-24 stores — so it is never persisted
                // on the strength of a status alone.
                services:
                    configuration.SERVICES.index === -1
                        ? undefined
                        : valueExtractor(
                              configuration.SERVICES,
                              invertedServices
                          ),
            };
            stores.push(store);
        }
    }

    return {
        stores,
        warnings: {
            missingColumns: configuration.getMissingFields(),
            unknownColumns,
        },
    };
};

export const batchStoreStatusOptions = {
    'open-24': 'Open 24',
    open: 'open',
    closed: 'closed',
};

export const storeCarWashStatusOptions = {
    open: 'Open',
    closed: 'Closed',
    'no-car-wash': 'No Car Wash',
};

export function WeekdayTag({
    weekday,
    ...tagProps
}: {
    weekday: string;
} & Omit<React.ComponentProps<typeof Tag>, 'children'>) {
    const variant = (() => {
        switch (weekday) {
            case 'Monday':
                return 'brand-blue';
            case 'Tuesday':
                return 'orange';
            case 'Wednesday':
                return 'blue';
            case 'Thursday':
                return 'error';
            case 'Friday':
                return 'purple';
            case 'Saturday':
            case 'Sunday':
                return 'success';
            default:
                return 'grey';
        }
    })();

    return (
        <Tag variant={variant} {...tagProps}>
            {weekday}
        </Tag>
    );
}

export const WEEKDAYS = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];

const WEEKDAYS_FRENCH = [
    'dimanche',
    'lundi',
    'mardi',
    'mercredi',
    'jeudi',
    'vendredi',
    'samedi',
];

const CarWashFrenchStatus = {
    open: 'Ouvert',
    closed: 'Fermé',
    'no-car-wash': 'Aucun lave-auto',
} as const;

function englishStatusToFrench(status: string) {
    if (status in FrenchStatus) {
        return FrenchStatus[status as keyof typeof FrenchStatus];
    }
    return status;
}

function englishCarWashStatusToFrench(status: string) {
    if (status in CarWashFrenchStatus) {
        return CarWashFrenchStatus[status as keyof typeof CarWashFrenchStatus];
    }
    return status;
}

function serviceMapper(service: string) {
    if (service in services) {
        return services[service as keyof typeof services];
    }
    return service;
}

export const storesToJson = (stores: Store[]) => {
    const data = stores.map((store) => {
        const hoursData: { [key: string]: string } = {};
        // Status-implied hours: open-24 is always 0:00-0:00 and
        // closed is always empty. Emit those regardless of stored per-day hours
        // so the roundtrip is stable (import derives the same from status).
        const is24 = store.status === StoreStatuskeys.OPEN_24;
        const isClosed = store.status === StoreStatuskeys.CLOSED;

        WEEKDAYS_FRENCH.forEach((day, index) => {
            const hoursOfTheDay = is24
                ? ALWAYS_OPEN_HOURS
                : isClosed
                ? ''
                : store.hours[index];
            if (hoursOfTheDay) {
                const [open, close] = hoursOfTheDay.split('-');
                hoursData[`${day} - ouverture`] = open;
                hoursData[`${day} - fermeture`] = close;
            } else {
                hoursData[`${day} - ouverture`] = '';
                hoursData[`${day} - fermeture`] = '';
            }
        });

        // The 24h marker service is implied by open-24 status and
        // derived-only: emitted here from status (whether or not it is stored),
        // never synthesized on import, ignored by the diff on open-24 stores.
        const exportedServices = is24
            ? [
                  ALWAYS_OPEN_SERVICE,
                  ...store.services.filter((s) => s !== ALWAYS_OPEN_SERVICE),
              ]
            : store.services;

        return {
            'numéro magasin': store.nref,
            marque: STORE_BRAND_LABELS[store.brand as StoreBrand] ?? '',
            adresse: store.addr.street || '',
            ville: store.addr.city || '',
            'code postal': store.addr.post || '',
            latitude: store.lat ?? '',
            longitude: store.lng ?? '',
            téléphone: store.tel || '',
            'statut du lave-auto': englishCarWashStatusToFrench(
                store.carWashStatus
            ),
            statut: englishStatusToFrench(store.status),
            ...hoursData,
            services: exportedServices.map(serviceMapper).join(', '),
        };
    });

    return data;
};

// Threshold below which a lat/lng difference is considered a CSV rounding artifact
// rather than an intentional edit (1e-5 degrees ≈ 1 m at the equator).
export const COORD_SUSPECT_THRESHOLD = 1e-5;

// Returns a map of nref → { lat?, lng? } for Difference entries where the
// coordinate changed but by less than COORD_SUSPECT_THRESHOLD — likely a
// floating-point rounding artifact from CSV export/import.
export function findSuspectCoordChanges(
    differences: DifferenceMap
): Map<string, { lat?: number; lng?: number }> {
    const suspects = new Map<string, { lat?: number; lng?: number }>();
    for (const [nref, diff] of differences) {
        const entry: { lat?: number; lng?: number } = {};
        if (
            diff.lat !== undefined &&
            diff.initial.lat !== null &&
            diff.initial.lat !== undefined &&
            Math.abs(diff.initial.lat - diff.lat) < COORD_SUSPECT_THRESHOLD
        ) {
            entry.lat = diff.lat;
        }
        if (
            diff.lng !== undefined &&
            diff.initial.lng !== null &&
            diff.initial.lng !== undefined &&
            Math.abs(diff.initial.lng - diff.lng) < COORD_SUSPECT_THRESHOLD
        ) {
            entry.lng = diff.lng;
        }
        if (entry.lat !== undefined || entry.lng !== undefined) {
            suspects.set(nref, entry);
        }
    }
    return suspects;
}

// Converts a CSV-parsed store (whose fields are all optional) into the strict
// Store shape used everywhere downstream. Missing optional fields fall back to
// `null`; missing required collections fall back to empty values. Status fields
// fall back to `''` and will be rejected by the backend on upload — the goal
// here is type honesty, not silent data fabrication.
export const parsedStoreToStore = (p: ParsedStore): Store => ({
    id: p.id,
    nref: p.nref,
    title: null,
    img: null,
    brand: p.brand,
    lat: p.lat ?? null,
    lng: p.lng ?? null,
    tel: p.tel ?? null,
    hours: p.hours.map((h) => h ?? null),
    services: p.services ?? [],
    status: p.status ?? '',
    carWashStatus: p.carWashStatus ?? '',
    addr: {
        street: p.addr.street ?? null,
        city: p.addr.city ?? null,
        post: p.addr.post ?? null,
    },
});

// Returns a new ParsedStore[] where, for each nref in keepDbNrefs, the lat/lng
// from the existing DB store overrides the parsed (CSV) coordinates. Used after
// the user reviews sub-1m coordinate changes in CoordPrecisionModal.
export const applyCoordOverrides = (
    parsed: ParsedStore[],
    stores: Store[],
    keepDbNrefs: Set<string>
): ParsedStore[] =>
    parsed.map((p) => {
        if (!keepDbNrefs.has(p.nref)) return p;
        const existing = stores.find((s) => s.nref === p.nref);
        if (!existing) return p;
        return {
            ...p,
            lat: existing.lat ?? p.lat,
            lng: existing.lng ?? p.lng,
        };
    });
